import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { tenantAPI, userAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Building2,
  ArrowLeft,
  Save,
  UserPlus,
  Loader2,
  Settings,
  Users,
} from "lucide-react";

const TenantDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    subscription_plan: "starter",
    max_employees: 50,
    enabled_modules: [],
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [tenantRes, usersRes] = await Promise.all([
        tenantAPI.getById(id),
        userAPI.getAll(),
      ]);
      const tenantData = tenantRes.data;
      setTenant(tenantData);
      setUsers(usersRes.data);
      setFormData({
        name: tenantData.name,
        subscription_plan: tenantData.subscription_plan,
        max_employees: tenantData.max_employees,
        enabled_modules: tenantData.enabled_modules,
      });
    } catch (error) {
      toast.error("Failed to fetch tenant details");
      navigate("/tenants");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await tenantAPI.update(id, formData);
      toast.success("Tenant updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update tenant");
    } finally {
      setSaving(false);
    }
  };

  const handleAssignHRAdmin = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }
    try {
      await tenantAPI.assignHRAdmin(id, selectedUserId);
      toast.success("HR Admin assigned successfully");
      setShowAssignDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign HR Admin");
    }
  };

  const modules = [
    "attendance",
    "leaves",
    "documents",
    "salary",
    "notices",
    "resignations",
    "geo_locations",
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/tenants")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">{tenant.name}</h1>
              <p className="text-slate-500">{tenant.domain}</p>
            </div>
          </div>
        </div>
        <Badge variant={tenant.status === "active" ? "default" : "destructive"} className="capitalize text-sm px-3 py-1">
          {tenant.status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Tenant Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Organization Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="tenant-name-input"
                />
              </div>
              <div className="form-group">
                <Label>Subscription Plan</Label>
                <Select
                  value={formData.subscription_plan}
                  onValueChange={(value) => setFormData({ ...formData, subscription_plan: value })}
                >
                  <SelectTrigger data-testid="tenant-plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Max Employees</Label>
                <Input
                  type="number"
                  value={formData.max_employees}
                  onChange={(e) => setFormData({ ...formData, max_employees: parseInt(e.target.value) })}
                  data-testid="tenant-max-employees-input"
                />
              </div>
              <div className="form-group">
                <Label>Created At</Label>
                <Input value={formatDate(tenant.created_at)} disabled />
              </div>
            </div>

            <div className="form-group">
              <Label>Enabled Modules</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {modules.map((module) => (
                  <Badge
                    key={module}
                    variant={formData.enabled_modules.includes(module) ? "default" : "outline"}
                    className="cursor-pointer capitalize"
                    onClick={() => {
                      const newModules = formData.enabled_modules.includes(module)
                        ? formData.enabled_modules.filter((m) => m !== module)
                        : [...formData.enabled_modules, module];
                      setFormData({ ...formData, enabled_modules: newModules });
                    }}
                  >
                    {module.replace("_", " ")}
                  </Badge>
                ))}
              </div>
            </div>

            <Button onClick={handleSave} className="btn-primary" disabled={saving} data-testid="save-tenant-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* HR Admin */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <Users className="w-5 h-5" />
                HR Admin
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tenant.hr_admin_id ? (
                <div className="text-center py-4">
                  <div className="avatar avatar-lg mx-auto mb-3">HR</div>
                  <p className="font-medium">HR Admin Assigned</p>
                  <p className="text-sm text-slate-500">ID: {tenant.hr_admin_id}</p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-500 mb-4">No HR Admin assigned</p>
                  <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full" data-testid="assign-hr-btn">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Assign HR Admin
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Assign HR Admin</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="form-group">
                          <Label>Select User</Label>
                          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                            <SelectTrigger data-testid="select-hr-user">
                              <SelectValue placeholder="Select a user" />
                            </SelectTrigger>
                            <SelectContent>
                              {users.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  {user.first_name} {user.last_name} ({user.email})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleAssignHRAdmin} className="w-full btn-primary" data-testid="confirm-assign-hr">
                          Assign HR Admin
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base">Quick Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-slate-500">Status</span>
                <Badge variant={tenant.status === "active" ? "default" : "destructive"} className="capitalize">
                  {tenant.status}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Plan</span>
                <span className="font-medium capitalize">{tenant.subscription_plan}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Max Employees</span>
                <span className="font-medium">{tenant.max_employees}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Modules</span>
                <span className="font-medium">{tenant.enabled_modules.length}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TenantDetailPage;
